import React, { useState, useRef } from 'react';
import Typography from '../components/Text/Typography';
import Colors from '../components/Colors/Colors';
import Icon from '../components/Icon/Icon';
import Button from '../components/Button/Button';
import ButtonGroup from '../components/Button/ButtonGroup';
import Toggle from '../components/Toggle/Toggle';
import Checkbox from '../components/Checkbox/Checkbox';
import CheckboxGroup from '../components/Checkbox/CheckboxGroup';
import Radio from '../components/Radio/Radio';
import RadioGroup from '../components/Radio/RadioGroup';
import Chip from '../components/Chip/Chip';
import Tag from '../components/Tag/Tag';
import Banner from '../components/Banner/Banner';
import TextInput from '../components/Text/TextInput';
import Sidebar from '../components/Sidebar/Sidebar';
import ProductCard from '../components/ProductCard/ProductCard';
import RoutineCard from '../components/RoutineCard/RoutineCard';
import Popover from '../components/Popover/Popover';
import IconButton from '../components/Button/IconButton';
import ModalDialog from '../components/Dialog/ModalDialog';
import ResponsiveDialog from '../components/ResponsiveDialog/ResponsiveDialog';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';
import './DesignSystem.css';

const DesignSystem: React.FC = () => {
  const [selectedColor, setSelectedColor] = useState<string>('primary-green');
  const [selectedIcon, setSelectedIcon] = useState<string>('search');
  const [selectedTypography, setSelectedTypography] = useState<string>('h1');
  const [buttonGroupSelection, setButtonGroupSelection] = useState<string>('Button');
  const [buttonGroupSelection2, setButtonGroupSelection2] = useState<string>('Yes');
  const [buttonGroupSelection3, setButtonGroupSelection3] = useState<string>('Save');
  const [buttonGroupSelection4, setButtonGroupSelection4] = useState<string>('Yes');
  const [buttonGroupSelection5, setButtonGroupSelection5] = useState<string>('Option 1');
  const [buttonGroupSelection6, setButtonGroupSelection6] = useState<string>('Option 1');
  const [toggleStates, setToggleStates] = useState({
    toggle1: false,
    toggle2: true,
    toggle3: false,
    toggle4: true
  });
  const [checkboxStates, setCheckboxStates] = useState({
    checkbox1: false,
    checkbox2: true,
    checkbox3: false,
    checkbox4: true,
    checkbox5: false,
    checkbox6: true
  });
  const [radioStates, setRadioStates] = useState({
    gender: 'female',
    notification: 'email',
    theme: 'light'
  });
  const [checkboxGroupStates, setCheckboxGroupStates] = useState({
    interests: ['reading', 'gaming'],
    notifications: ['email', 'push'],
    features: ['dark-mode', 'notifications', 'analytics']
  });
  const [chipStates, setChipStates] = useState({
    selectedCategory: 'all',
    selectedTags: ['featured', 'popular'],
    selectedFilters: ['active', 'verified']
  });
  const [textInputStates, setTextInputStates] = useState({
    basic: '',
    withLabel: '',
    withError: '',
    placeholder: '',
    email: '',
    password: ''
  });
  const [popoverStates, setPopoverStates] = useState({
    basic: false,
    top: false,
    bottom: false,
    left: false,
    right: false,
    options: false
  });
  const [dialogStates, setDialogStates] = useState({
    basic: false,
    withContent: false,
    withActions: false,
    activeIngredients: false
  });

  // Refs for popover triggers
  const basicTriggerRef = useRef<HTMLButtonElement>(null);
  const topTriggerRef = useRef<HTMLButtonElement>(null);
  const bottomTriggerRef = useRef<HTMLButtonElement>(null);
  const leftTriggerRef = useRef<HTMLButtonElement>(null);
  const rightTriggerRef = useRef<HTMLButtonElement>(null);
  const optionsTriggerRef = useRef<HTMLButtonElement>(null);

  const popoverOptions = [
    { id: '1', label: 'Option Name', onClick: () => alert('Option 1 clicked!') },
    { id: '2', label: 'Option Name', onClick: () => alert('Option 2 clicked!') },
    { id: '3', label: 'Option Name', onClick: () => alert('Option 3 clicked!') },
    { id: '4', label: 'Option Name', onClick: () => alert('Option 4 clicked!') }
  ];

  const allColors = [
    'primary-green', 'light-green', 'dark-green', 'white', 'black',
    'light-gray', 'dark-gray', 'gray', 'warning-yellow', 'warning-red',
    'pastelle-yellow', 'pastelle-red', 'pastelle-gray', 'pastelle-blue'
  ];

  const allIcons = [
    'close', 'add', 'refresh', 'edit', 'search', 'user', 'info', 'alert',
    'question', 'idea', 'trash', 'book', 'home', 'menu', 'settings',
    'dots', 'gentle', 'calendar-clock', 'chevron-left', 'chevron-right',
    'chevron-up', 'chevron-down', 'drag', 'adjustments', 'sparkles'
  ];

  const typographyVariants = [
    { name: 'h1', description: 'Heading 1' },
    { name: 'h2', description: 'Heading 2' },
    { name: 'h3', description: 'Heading 3' },
    { name: 'body-bold', description: 'Body Bold' },
    { name: 'body', description: 'Body' },
    { name: 'body-small-bold', description: 'Body Small Bold' },
    { name: 'body-small', description: 'Body Small' }
  ];

  return (
    <div className="design-system">
      <header className="design-system-header">
        <Typography variant="h1">Design System QA</Typography>
        <Typography variant="body">Comprehensive component testing and showcase</Typography>
      </header>

      {/* Typography Section */}
      <section className="design-system-section">
        <Typography variant="h2">Typography</Typography>
        <div className="component-grid">
          {typographyVariants.map(variant => (
            <div key={variant.name} className="component-card">
              <div className="component-preview">
                <Typography variant={variant.name as any}>
                  {variant.description}
                </Typography>
              </div>
              <div className="component-info">
                <Typography variant="body-small-bold">{variant.name}</Typography>
                <Typography variant="body-small">{variant.description}</Typography>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Colors Section */}
      <section className="design-system-section">
        <Typography variant="h2">Colors</Typography>
        <div className="color-grid">
          {allColors.map(color => (
            <div 
              key={color} 
              className={`color-card ${selectedColor === color ? 'selected' : ''}`}
              onClick={() => setSelectedColor(color)}
            >
              <div 
                className="color-preview" 
                style={{ backgroundColor: `var(--color-${color.replace('-', '-')})` }}
              />
              <div className="color-info">
                <Typography variant="body-small-bold">{color}</Typography>
                <Typography variant="body-small" className="color-value">
                  var(--color-{color.replace('-', '-')})
                </Typography>
              </div>
            </div>
          ))}
        </div>
        
        <div className="color-test-area">
          <Typography variant="h3">Color Test Area</Typography>
          <div className="color-test-content">
            <Typography variant="body" style={{ color: `var(--color-${selectedColor.replace('-', '-')})` }}>
              This text uses the selected color: {selectedColor}
            </Typography>
            <div 
              className="color-test-background"
              style={{ backgroundColor: `var(--color-${selectedColor.replace('-', '-')})` }}
            >
              <Typography variant="body" style={{ color: selectedColor.includes('white') || selectedColor.includes('light') ? '#000' : '#fff' }}>
                Background test with {selectedColor}
              </Typography>
            </div>
          </div>
        </div>
      </section>

      {/* Icons Section */}
      <section className="design-system-section">
        <Typography variant="h2">Icons</Typography>
        <div className="icon-grid">
          {allIcons.map(icon => (
            <div 
              key={icon} 
              className={`icon-card ${selectedIcon === icon ? 'selected' : ''}`}
              onClick={() => setSelectedIcon(icon)}
            >
              <div className="icon-preview">
                <Icon name={icon as any} size="large" />
              </div>
              <div className="icon-info">
                <Typography variant="body-small-bold">{icon}</Typography>
              </div>
            </div>
          ))}
        </div>

        <div className="icon-test-area">
          <Typography variant="h3">Icon Test Area</Typography>
          <div className="icon-test-content">
            <div className="icon-size-test">
              <Typography variant="body-small-bold">Small Size:</Typography>
              <Icon name={selectedIcon as any} size="small" />
            </div>
            <div className="icon-size-test">
              <Typography variant="body-small-bold">Large Size:</Typography>
              <Icon name={selectedIcon as any} size="large" />
            </div>
            <div className="icon-color-test">
              <Typography variant="body-small-bold">Colored Icons:</Typography>
              <Icon name={selectedIcon as any} size="large" colorVariant="primary-green" />
              <Icon name={selectedIcon as any} size="large" colorVariant="warning-red" />
              <Icon name={selectedIcon as any} size="large" colorVariant="warning-yellow" />
            </div>
          </div>
        </div>
      </section>

      {/* Buttons Section */}
      <section className="design-system-section">
        <Typography variant="h2">Buttons</Typography>
        <div className="button-test-area">
          <div className="button-variants">
            <Typography variant="h3">Button Variants</Typography>
            <div className="button-row">
              <Button variant="primary">Primary Button</Button>
              <Button variant="secondary">Secondary Button</Button>
              <Button variant="text">Text Button</Button>
            </div>
          </div>

          <div className="button-states">
            <Typography variant="h3">Button States</Typography>
            <div className="button-row">
              <Button variant="primary">Normal</Button>
              <Button variant="primary" disabled>Disabled</Button>
            </div>
            <div className="button-row">
              <Button variant="secondary">Normal</Button>
              <Button variant="secondary" disabled>Disabled</Button>
            </div>
            <div className="button-row">
              <Button variant="text">Normal</Button>
              <Button variant="text" disabled>Disabled</Button>
            </div>
          </div>

          <div className="button-with-icons">
            <Typography variant="h3">Buttons with Icons</Typography>
            <div className="button-row">
              <Button variant="primary" icon={{ name: "add", size: "small" }}>
                Add Item
              </Button>
              <Button variant="secondary" icon={{ name: "edit", size: "small" }}>
                Edit
              </Button>
              <Button variant="text" icon={{ name: "trash", size: "small" }}>
                Delete
              </Button>
            </div>
          </div>

          <div className="button-group-examples">
            <Typography variant="h3">Button Groups</Typography>
                          <div className="button-group-row">
                <div className="button-group-demo">
                  <Typography variant="body-small-bold">Basic ButtonGroup (matches design):</Typography>
                  <ButtonGroup
                    options={['Button', 'Button']}
                    value={buttonGroupSelection}
                    onChange={(value) => setButtonGroupSelection(value)}
                  />
                </div>
              </div>
            
                          <div className="button-group-row">
                <div className="button-group-demo">
                  <Typography variant="body-small-bold">Different Options:</Typography>
                  <ButtonGroup
                    options={['Yes', 'No']}
                    value={buttonGroupSelection2}
                    onChange={(value) => setButtonGroupSelection2(value)}
                  />
                </div>
                <div className="button-group-demo">
                  <Typography variant="body-small-bold">Action Buttons:</Typography>
                  <ButtonGroup
                    options={['Save', 'Cancel']}
                    value={buttonGroupSelection3}
                    onChange={(value) => setButtonGroupSelection3(value)}
                  />
                </div>
              </div>

            <div className="button-group-row">
              <div className="button-group-demo">
                <Typography variant="body-small-bold">Three Options:</Typography>
                <ButtonGroup
                  options={['Yes', 'Maybe', 'No']}
                  value={buttonGroupSelection4}
                  onChange={(value) => setButtonGroupSelection4(value)}
                />
              </div>
              <div className="button-group-demo">
                <Typography variant="body-small-bold">More Options:</Typography>
                <ButtonGroup
                  options={['Option 1', 'Option 2', 'Option 3', 'Option 4']}
                  value={buttonGroupSelection5}
                  onChange={(value) => setButtonGroupSelection5(value)}
                />
              </div>
            </div>
            
            <div className="button-group-row">
              <div className="button-group-demo">
                <Typography variant="body-small-bold">Default Selection (first button):</Typography>
                <ButtonGroup
                  options={['Option 1', 'Option 2']}
                  value={buttonGroupSelection6}
                  onChange={(value) => setButtonGroupSelection6(value)}
                />
              </div>
            </div>
            
            <div className="button-group-row">
              <div className="button-group-demo">
                <Typography variant="body-small-bold">Interactive Test (click to switch):</Typography>
                <ButtonGroup
                  options={['Button', 'Button']}
                  value={buttonGroupSelection}
                  onChange={(value) => {
                    console.log('Button selected:', value);
                    setButtonGroupSelection(value);
                  }}
                />
                <Typography variant="body-small" style={{ marginTop: '8px', color: 'var(--color-dark-gray)' }}>
                  Current selection: {buttonGroupSelection}. Click buttons to see selection change.
                </Typography>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* IconButton Section */}
      <section className="design-system-section">
        <Typography variant="h2">Icon Buttons</Typography>
        <div className="iconbutton-test-area">
          <div className="iconbutton-basic">
            <Typography variant="h3">Basic Icon Buttons</Typography>
            <div className="iconbutton-row">
              <IconButton
                icon={<Icon name="edit" size="small" />}
                onClick={() => alert('Edit clicked!')}
                ariaLabel="Edit"
              />
              <IconButton
                icon={<Icon name="add" size="small" />}
                onClick={() => alert('Add clicked!')}
                ariaLabel="Add"
              />
              <IconButton
                icon={<Icon name="settings" size="small" />}
                onClick={() => alert('Settings clicked!')}
                ariaLabel="Settings"
              />
              <IconButton
                icon={<Icon name="trash" size="small" />}
                onClick={() => alert('Delete clicked!')}
                ariaLabel="Delete"
              />
              <IconButton
                icon={<Icon name="close" size="small" />}
                onClick={() => alert('Close clicked!')}
                ariaLabel="Close"
              />
            </div>
          </div>

          <div className="iconbutton-sizes">
            <Typography variant="h3">Icon Button Sizes</Typography>
            <div className="iconbutton-row">
              <IconButton
                icon={<Icon name="search" size="small" />}
                onClick={() => alert('Small search clicked!')}
                ariaLabel="Search (small)"
              />
              <IconButton
                icon={<Icon name="search" size="large" />}
                onClick={() => alert('Large search clicked!')}
                ariaLabel="Search (large)"
              />
            </div>
          </div>

          <div className="iconbutton-states">
            <Typography variant="h3">Icon Button States</Typography>
            <div className="iconbutton-row">
              <IconButton
                icon={<Icon name="edit" size="large" />}
                onClick={() => alert('Edit clicked!')}
                ariaLabel="Edit"
              />
              <IconButton
                icon={<Icon name="edit" size="large" />}
                onClick={() => alert('Edit clicked!')}
                disabled
                ariaLabel="Edit (disabled)"
              />
            </div>
          </div>

          <div className="iconbutton-examples">
            <Typography variant="h3">Common Use Cases</Typography>
            <div className="iconbutton-row">
              <IconButton
                icon={<Icon name="add" size="large" />}
                onClick={() => alert('Add product clicked!')}
                ariaLabel="Add product"
              />
              <IconButton
                icon={<Icon name="edit" size="small" />}
                onClick={() => alert('Edit product clicked!')}
                ariaLabel="Edit product"
              />
              <IconButton
                icon={<Icon name="trash" size="small" />}
                onClick={() => alert('Delete product clicked!')}
                ariaLabel="Delete product"
              />
              <IconButton
                icon={<Icon name="close" size="small" />}
                onClick={() => alert('Close dialog clicked!')}
                ariaLabel="Close dialog"
              />
              <IconButton
                icon={<Icon name="refresh" size="large" />}
                onClick={() => alert('Refresh clicked!')}
                ariaLabel="Refresh"
              />
            </div>
          </div>

          <div className="iconbutton-custom">
            <Typography variant="h3">Custom Icons</Typography>
            <div className="iconbutton-row">
              <IconButton
                icon={<Icon name="user" size="large" colorVariant="primary-green" />}
                onClick={() => alert('User profile clicked!')}
                ariaLabel="User profile"
              />
              <IconButton
                icon={<Icon name="info" size="small" colorVariant="warning-yellow" />}
                onClick={() => alert('Info clicked!')}
                ariaLabel="Information"
              />
              <IconButton
                icon={<Icon name="alert" size="large" colorVariant="warning-red" />}
                onClick={() => alert('Alert clicked!')}
                ariaLabel="Alert"
              />
            </div>
          </div>

          <div className="iconbutton-usage">
            <Typography variant="h3">Usage Examples</Typography>
            <div className="code-examples">
              <div className="code-example">
                <Typography variant="body-small-bold">Basic Usage:</Typography>
                <pre style={{ backgroundColor: '#f5f5f5', padding: '12px', borderRadius: '4px', fontSize: '12px' }}>
{`<IconButton
  icon={<Icon name="close" size="small" />}
  onClick={handleClose}
  ariaLabel="Close dialog"
/>`}
                </pre>
              </div>
              <div className="code-example">
                <Typography variant="body-small-bold">Large Icon:</Typography>
                <pre style={{ backgroundColor: '#f5f5f5', padding: '12px', borderRadius: '4px', fontSize: '12px' }}>
{`<IconButton
  icon={<Icon name="search" size="large" />}
  onClick={handleSearch}
  ariaLabel="Search"
/>`}
                </pre>
              </div>
              <div className="code-example">
                <Typography variant="body-small-bold">Custom Styling:</Typography>
                <pre style={{ backgroundColor: '#f5f5f5', padding: '12px', borderRadius: '4px', fontSize: '12px' }}>
{`<IconButton
  icon={<Icon name="add" size="large" colorVariant="primary-green" />}
  onClick={handleAdd}
  className="custom-button"
  ariaLabel="Add item"
/>`}
                </pre>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Toggle Section */}
      <section className="design-system-section">
        <Typography variant="h2">Toggle Switches</Typography>
        <div className="toggle-test-area">
          <div className="toggle-variants">
            <Typography variant="h3">Toggle States</Typography>
            <div className="toggle-row">
              <Toggle
                checked={toggleStates.toggle1}
                onChange={(checked) => setToggleStates(prev => ({ ...prev, toggle1: checked }))}
                label="Notifications"
                labelVariant="body"
              />
              <Toggle
                checked={toggleStates.toggle2}
                onChange={(checked) => setToggleStates(prev => ({ ...prev, toggle2: checked }))}
                label="Dark Mode"
                labelVariant="body"
              />
            </div>
            <div className="toggle-row">
              <Toggle
                checked={toggleStates.toggle3}
                onChange={(checked) => setToggleStates(prev => ({ ...prev, toggle3: checked }))}
                label="Auto-save"
                labelVariant="body-small"
                disabled
              />
              <Toggle
                checked={toggleStates.toggle4}
                onChange={(checked) => setToggleStates(prev => ({ ...prev, toggle4: checked }))}
                label="Sound Effects"
                labelVariant="body-small"
                disabled
              />
            </div>
          </div>

          <div className="toggle-typography">
            <Typography variant="h3">Typography Variants</Typography>
            <div className="toggle-row">
              <Toggle
                checked={toggleStates.toggle1}
                onChange={(checked) => setToggleStates(prev => ({ ...prev, toggle1: checked }))}
                label="Body Text (Default)"
                labelVariant="body"
              />
              <Toggle
                checked={toggleStates.toggle2}
                onChange={(checked) => setToggleStates(prev => ({ ...prev, toggle2: checked }))}
                label="Small Text"
                labelVariant="body-small"
              />
              <Toggle
                checked={toggleStates.toggle3}
                onChange={(checked) => setToggleStates(prev => ({ ...prev, toggle3: checked }))}
                label="Small Bold Text"
                labelVariant="body-small-bold"
              />
            </div>
          </div>

          <div className="toggle-interactive">
            <Typography variant="h3">Interactive Toggles</Typography>
            <div className="toggle-row">
              <Toggle
                checked={toggleStates.toggle1}
                onChange={(checked) => {
                  setToggleStates(prev => ({ ...prev, toggle1: checked }));
                  alert(`Notifications ${checked ? 'enabled' : 'disabled'}!`);
                }}
                label="Click to test interaction"
                labelVariant="body"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Checkbox Section */}
      <section className="design-system-section">
        <Typography variant="h2">Checkboxes</Typography>
        <div className="checkbox-test-area">
          <div className="checkbox-variants">
            <Typography variant="h3">Checkbox States</Typography>
            <div className="checkbox-row">
              <Checkbox
                checked={checkboxStates.checkbox1}
                onChange={(checked) => setCheckboxStates(prev => ({ ...prev, checkbox1: checked }))}
                label="Accept terms and conditions"
                labelVariant="body"
              />
              <Checkbox
                checked={checkboxStates.checkbox2}
                onChange={(checked) => setCheckboxStates(prev => ({ ...prev, checkbox2: checked }))}
                label="Subscribe to newsletter"
                labelVariant="body"
              />
            </div>
            <div className="checkbox-row">
              <Checkbox
                checked={checkboxStates.checkbox3}
                onChange={(checked) => setCheckboxStates(prev => ({ ...prev, checkbox3: checked }))}
                label="Remember me"
                labelVariant="body-small"
                disabled
              />
              <Checkbox
                checked={checkboxStates.checkbox4}
                onChange={(checked) => setCheckboxStates(prev => ({ ...prev, checkbox4: checked }))}
                label="Auto-login"
                labelVariant="body-small"
                disabled
              />
            </div>
          </div>

          <div className="checkbox-typography">
            <Typography variant="h3">Typography Variants</Typography>
            <div className="checkbox-row">
              <Checkbox
                checked={checkboxStates.checkbox1}
                onChange={(checked) => setCheckboxStates(prev => ({ ...prev, checkbox1: checked }))}
                label="Body Text (Default)"
                labelVariant="body"
              />
              <Checkbox
                checked={checkboxStates.checkbox2}
                onChange={(checked) => setCheckboxStates(prev => ({ ...prev, checkbox2: checked }))}
                label="Small Text"
                labelVariant="body-small"
              />
              <Checkbox
                checked={checkboxStates.checkbox3}
                onChange={(checked) => setCheckboxStates(prev => ({ ...prev, checkbox3: checked }))}
                label="Small Bold Text"
                labelVariant="body-small-bold"
              />
            </div>
          </div>

          <div className="checkbox-interactive">
            <Typography variant="h3">Interactive Checkboxes</Typography>
            <div className="checkbox-row">
              <Checkbox
                checked={checkboxStates.checkbox5}
                onChange={(checked) => {
                  setCheckboxStates(prev => ({ ...prev, checkbox5: checked }));
                  alert(`Checkbox ${checked ? 'checked' : 'unchecked'}!`);
                }}
                label="Click to test interaction"
                labelVariant="body"
              />
            </div>
          </div>

          <div className="checkbox-group-example">
            <Typography variant="h3">Checkbox Group Example</Typography>
            <div className="checkbox-group-section">
              <Typography variant="body-bold">Interests:</Typography>
              <CheckboxGroup
                name="interests"
                value={checkboxGroupStates.interests}
                onChange={(value) => setCheckboxGroupStates(prev => ({ ...prev, interests: value }))}
                options={[
                  { value: 'reading', label: 'Reading' },
                  { value: 'gaming', label: 'Gaming' },
                  { value: 'music', label: 'Music' },
                  { value: 'sports', label: 'Sports' },
                  { value: 'cooking', label: 'Cooking', disabled: true }
                ]}
                labelVariant="body"
              />
            </div>

            <div className="checkbox-group-section">
              <Typography variant="body-bold">Notification Preferences:</Typography>
              <CheckboxGroup
                name="notifications"
                value={checkboxGroupStates.notifications}
                onChange={(value) => setCheckboxGroupStates(prev => ({ ...prev, notifications: value }))}
                options={[
                  { value: 'email', label: 'Email notifications' },
                  { value: 'push', label: 'Push notifications' },
                  { value: 'sms', label: 'SMS notifications' },
                  { value: 'in-app', label: 'In-app notifications' }
                ]}
                labelVariant="body-small"
              />
            </div>

            <div className="checkbox-group-section">
              <Typography variant="body-bold">Feature Toggles:</Typography>
              <CheckboxGroup
                name="features"
                value={checkboxGroupStates.features}
                onChange={(value) => setCheckboxGroupStates(prev => ({ ...prev, features: value }))}
                options={[
                  { value: 'dark-mode', label: 'Dark Mode' },
                  { value: 'notifications', label: 'Notifications' },
                  { value: 'analytics', label: 'Analytics' },
                  { value: 'beta-features', label: 'Beta Features', disabled: true }
                ]}
                labelVariant="body-small-bold"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Radio Section */}
      <section className="design-system-section">
        <Typography variant="h2">Radio Buttons</Typography>
        <div className="radio-test-area">
          <div className="radio-variants">
            <Typography variant="h3">Radio States</Typography>
            <div className="radio-row">
              <Radio
                checked={radioStates.gender === 'male'}
                onChange={() => setRadioStates(prev => ({ ...prev, gender: 'male' }))}
                label="Male"
                name="gender"
                value="male"
                labelVariant="body"
              />
              <Radio
                checked={radioStates.gender === 'female'}
                onChange={() => setRadioStates(prev => ({ ...prev, gender: 'female' }))}
                label="Female"
                name="gender"
                value="female"
                labelVariant="body"
              />
              <Radio
                checked={radioStates.gender === 'other'}
                onChange={() => setRadioStates(prev => ({ ...prev, gender: 'other' }))}
                label="Other"
                name="gender"
                value="other"
                labelVariant="body"
                disabled
              />
            </div>
          </div>

          <div className="radio-typography">
            <Typography variant="h3">Typography Variants</Typography>
            <div className="radio-row">
              <Radio
                checked={radioStates.notification === 'email'}
                onChange={() => setRadioStates(prev => ({ ...prev, notification: 'email' }))}
                label="Email notifications"
                name="notification"
                value="email"
                labelVariant="body"
              />
              <Radio
                checked={radioStates.notification === 'sms'}
                onChange={() => setRadioStates(prev => ({ ...prev, notification: 'sms' }))}
                label="SMS notifications"
                name="notification"
                value="sms"
                labelVariant="body-small"
              />
              <Radio
                checked={radioStates.notification === 'push'}
                onChange={() => setRadioStates(prev => ({ ...prev, notification: 'push' }))}
                label="Push notifications"
                name="notification"
                value="push"
                labelVariant="body-small-bold"
              />
            </div>
          </div>

          <div className="radio-group-example">
            <Typography variant="h3">Radio Group Example</Typography>
            <RadioGroup
              name="theme"
              value={radioStates.theme}
              onChange={(value) => setRadioStates(prev => ({ ...prev, theme: value }))}
              options={[
                { value: 'light', label: 'Light Theme' },
                { value: 'dark', label: 'Dark Theme' },
                { value: 'auto', label: 'Auto (System)' },
                { value: 'custom', label: 'Custom Theme', disabled: true }
              ]}
              labelVariant="body"
            />
          </div>

          <div className="radio-interactive">
            <Typography variant="h3">Interactive Radio</Typography>
            <div className="radio-row">
              <Radio
                checked={radioStates.gender === 'male'}
                onChange={(checked) => {
                  if (checked) {
                    setRadioStates(prev => ({ ...prev, gender: 'male' }));
                    alert('Male selected!');
                  }
                }}
                label="Click to test interaction"
                name="interactive"
                value="male"
                labelVariant="body"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Chip Section */}
      <section className="design-system-section">
        <Typography variant="h2">Chips</Typography>
        <div className="chip-test-area">
          <div className="chip-examples">
            <Typography variant="h3">Chip Examples</Typography>
            <div className="chip-row">
              <Chip
                label="Normal"
                isSelected={false}
                onClick={() => alert('Chip clicked!')}
              />
              <Chip
                label="Selected"
                isSelected={true}
                onClick={() => alert('Selected chip clicked!')}
              />
            </div>
          </div>

          <div className="chip-states">
            <Typography variant="h3">States</Typography>
            <div className="chip-row">
              <Chip
                label="Normal"
                isSelected={false}
                onClick={() => alert('Normal chip clicked!')}
              />
              <Chip
                label="Selected"
                isSelected={true}
                onClick={() => alert('Selected chip clicked!')}
              />
              <Chip
                label="Disabled"
                isSelected={false}
                disabled
              />
            </div>
          </div>

          <div className="chip-multi-select">
            <Typography variant="h3">Multi-Select Example</Typography>
            <div className="chip-row">
              <Chip
                label="Featured"
                isSelected={chipStates.selectedTags.includes('featured')}
                onClick={() => {
                  const newTags = chipStates.selectedTags.includes('featured')
                    ? chipStates.selectedTags.filter(tag => tag !== 'featured')
                    : [...chipStates.selectedTags, 'featured'];
                  setChipStates(prev => ({ ...prev, selectedTags: newTags }));
                }}
              />
              <Chip
                label="Popular"
                isSelected={chipStates.selectedTags.includes('popular')}
                onClick={() => {
                  const newTags = chipStates.selectedTags.includes('popular')
                    ? chipStates.selectedTags.filter(tag => tag !== 'popular')
                    : [...chipStates.selectedTags, 'popular'];
                  setChipStates(prev => ({ ...prev, selectedTags: newTags }));
                }}
              />
              <Chip
                label="New"
                isSelected={chipStates.selectedTags.includes('new')}
                onClick={() => {
                  const newTags = chipStates.selectedTags.includes('new')
                    ? chipStates.selectedTags.filter(tag => tag !== 'new')
                    : [...chipStates.selectedTags, 'new'];
                  setChipStates(prev => ({ ...prev, selectedTags: newTags }));
                }}
              />
              <Chip
                label="Trending"
                isSelected={chipStates.selectedTags.includes('trending')}
                onClick={() => {
                  const newTags = chipStates.selectedTags.includes('trending')
                    ? chipStates.selectedTags.filter(tag => tag !== 'trending')
                    : [...chipStates.selectedTags, 'trending'];
                  setChipStates(prev => ({ ...prev, selectedTags: newTags }));
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Tag Section */}
      <section className="design-system-section">
        <Typography variant="h2">Tags</Typography>
        <div className="tag-test-area">
          <div className="tag-examples">
            <Typography variant="h3">Tag Examples</Typography>
            <div className="tag-row">
              <Tag>Skincare</Tag>
              <Tag>Cleanser</Tag>
              <Tag>Moisturizer</Tag>
              <Tag>Serum</Tag>
              <Tag>Sunscreen</Tag>
            </div>
          </div>

          <div className="tag-categories">
            <Typography variant="h3">Product Categories</Typography>
            <div className="tag-row">
              <Tag>Face</Tag>
              <Tag>Body</Tag>
              <Tag>Hair</Tag>
              <Tag>Makeup</Tag>
              <Tag>Tools</Tag>
            </div>
          </div>

          <div className="tag-ingredients">
            <Typography variant="h3">Ingredient Types</Typography>
            <div className="tag-row">
              <Tag>Hyaluronic Acid</Tag>
              <Tag>Vitamin C</Tag>
              <Tag>Retinol</Tag>
              <Tag>Niacinamide</Tag>
              <Tag>Peptides</Tag>
            </div>
          </div>

          <div className="tag-concerns">
            <Typography variant="h3">Skin Concerns</Typography>
            <div className="tag-row">
              <Tag>Acne</Tag>
              <Tag>Aging</Tag>
              <Tag>Dark Spots</Tag>
              <Tag>Dryness</Tag>
              <Tag>Oiliness</Tag>
            </div>
          </div>
        </div>
      </section>

      {/* Banner Section */}
      <section className="design-system-section">
        <Typography variant="h2">Banners</Typography>
        <div className="banner-test-area">
          <div className="banner-examples">
            <Typography variant="h3">Banner Variants</Typography>
            
            <Banner variant="info">
              This is an informational banner. Use this for general information and updates.
            </Banner>
            
            <Banner variant="tip">
              This is a tip banner. Use this to provide helpful suggestions and best practices.
            </Banner>
            
            <Banner variant="caution">
              This is a caution banner. Use this to warn users about potential issues or important considerations.
            </Banner>
            
            <Banner variant="warning">
              This is a warning banner. Use this for critical warnings that require immediate attention.
            </Banner>
          </div>

          <div className="banner-custom-titles">
            <Typography variant="h3">Custom Titles</Typography>
            
            <Banner variant="info" title="Important Update">
              We've made some changes to our privacy policy. Please review the updated terms.
            </Banner>
            
            <Banner variant="tip" title="Pro Tip">
              Apply sunscreen 15 minutes before sun exposure for maximum protection.
            </Banner>
            
            <Banner variant="caution" title="Before You Start">
              Make sure to patch test new products on a small area of skin first.
            </Banner>
            
            <Banner variant="warning" title="Critical Alert">
              This product contains ingredients that may cause allergic reactions in sensitive individuals.
            </Banner>
          </div>

          <div className="banner-long-content">
            <Typography variant="h3">Long Content</Typography>
            
            <Banner variant="info">
              This banner contains a longer message to demonstrate how the component handles extended content. 
              The text should wrap properly and maintain good readability while the icon stays properly aligned 
              at the top of the content area.
            </Banner>
            
            <Banner variant="tip">
              When using multiple skincare products, remember to layer them from thinnest to thickest consistency. 
              Start with cleanser, then toner, serum, moisturizer, and finally sunscreen during the day. 
              This ensures optimal absorption and effectiveness of each product.
            </Banner>
          </div>
        </div>
      </section>

      {/* TextInput Section */}
      <section className="design-system-section">
        <Typography variant="h2">Text Inputs</Typography>
        <div className="textinput-test-area">
          <div className="textinput-examples">
            <Typography variant="h3">Basic Input</Typography>
            <TextInput
              value={textInputStates.basic}
              onChange={(e) => setTextInputStates(prev => ({ ...prev, basic: e.target.value }))}
              placeholder="Enter some text..."
            />
          </div>

          <div className="textinput-examples">
            <Typography variant="h3">Input with Label</Typography>
            <TextInput
              value={textInputStates.withLabel}
              onChange={(e) => setTextInputStates(prev => ({ ...prev, withLabel: e.target.value }))}
              label="Product Name"
              placeholder="Enter product name..."
            />
          </div>

          <div className="textinput-examples">
            <Typography variant="h3">Input with Error</Typography>
            <TextInput
              value={textInputStates.withError}
              onChange={(e) => setTextInputStates(prev => ({ ...prev, withError: e.target.value }))}
              label="Email Address"
              placeholder="Enter your email..."
              error="Please enter a valid email address"
            />
          </div>

          <div className="textinput-examples">
            <Typography variant="h3">Different Input Types</Typography>
            <TextInput
              value={textInputStates.email}
              onChange={(e) => setTextInputStates(prev => ({ ...prev, email: e.target.value }))}
              label="Email"
              type="email"
              placeholder="user@example.com"
            />
            <TextInput
              value={textInputStates.password}
              onChange={(e) => setTextInputStates(prev => ({ ...prev, password: e.target.value }))}
              label="Password"
              type="password"
              placeholder="Enter your password..."
            />
          </div>

          <div className="textinput-examples">
            <Typography variant="h3">Placeholder Only</Typography>
            <TextInput
              value={textInputStates.placeholder}
              onChange={(e) => setTextInputStates(prev => ({ ...prev, placeholder: e.target.value }))}
              placeholder="This input only has a placeholder..."
            />
          </div>
        </div>
      </section>

      {/* Sidebar Section */}
      <section className="design-system-section">
        <Typography variant="h2">Sidebar Navigation</Typography>
        <div className="sidebar-test-area">
          <div className="sidebar-examples">
            <Typography variant="h3">Sidebar Component</Typography>
            <div className="sidebar-preview">
              <Sidebar />
            </div>
          </div>
          
          <div className="sidebar-info">
            <Typography variant="h3">Sidebar Features</Typography>
            <ul className="sidebar-features">
              <li>
                <Colors variant="black">
                  <Typography variant="body-small">
                    Uses Typography component for consistent text styling
                  </Typography>
                </Colors>
              </li>
              <li>
                <Colors variant="black">
                  <Typography variant="body-small">
                    Uses Colors component for consistent color theming
                  </Typography>
                </Colors>
              </li>
              <li>
                <Colors variant="black">
                  <Typography variant="body-small">
                    Active states use primary-green color
                  </Typography>
                </Colors>
              </li>
              <li>
                <Colors variant="black">
                  <Typography variant="body-small">
                    Section headers use gray color and bold small text
                  </Typography>
                </Colors>
              </li>
              <li>
                <Colors variant="black">
                  <Typography variant="body-small">
                    Hover effects with light-gray background
                  </Typography>
                </Colors>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* ProductCard Section */}
      <section className="design-system-section">
        <Typography variant="h2">Product Cards</Typography>
        <div className="card-test-area">
          <div className="card-examples">
            <Typography variant="h3">Product Card Examples</Typography>
            <div className="card-grid">
              <ProductCard
                company="CeraVe"
                productName="Hydrating Facial Cleanser"
                tag="Cleanser"
                productId={1}
                onDelete={(id) => alert(`Delete product ${id}`)}
              />
              <ProductCard
                company="The Ordinary"
                productName="Niacinamide 10% + Zinc 1%"
                tag="Serum"
                productId={2}
              />
              <ProductCard
                company="Neutrogena"
                productName="Ultra Sheer Dry-Touch Sunscreen SPF 55"
                tag="Sunscreen"
                productId={3}
                onDelete={(id) => alert(`Delete product ${id}`)}
              />
              <ProductCard
                company="La Roche-Posay"
                productName="Double Repair Face Moisturizer"
                tag="Moisturizer"
                productId={4}
              />
            </div>
          </div>

          <div className="card-variants">
            <Typography variant="h3">Product Card Variants</Typography>
            <div className="card-grid">
              <ProductCard
                company="Simple"
                productName="Kind to Skin Moisturizing Facial Wash"
                productId={5}
              />
              <ProductCard
                company="Cetaphil"
                productName="Daily Facial Cleanser"
                tag="Gentle"
                productId={6}
                onDelete={(id) => alert(`Delete product ${id}`)}
              />
            </div>
          </div>

          <div className="mobile-examples">
            <Typography variant="h3">Mobile Product Cards</Typography>
            <div className="mobile-preview">
              <div className="mobile-frame">
                <div className="mobile-content">
                  <div className="mobile-card-grid">
                    <ProductCard
                      company="CeraVe"
                      productName="Hydrating Facial Cleanser"
                      tag="Cleanser"
                      productId={7}
                      onDelete={(id) => alert(`Delete product ${id}`)}
                    />
                    <ProductCard
                      company="The Ordinary"
                      productName="Niacinamide 10% + Zinc 1%"
                      tag="Serum"
                      productId={8}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RoutineCard Section */}
      <section className="design-system-section">
        <Typography variant="h2">Routine Cards</Typography>
        <div className="card-test-area">
          <div className="card-examples">
            <Typography variant="h3">Routine Card Examples</Typography>
            <div className="routine-card-examples">
              <RoutineCard
                company="CeraVe"
                productName="Hydrating Facial Cleanser"
                category="Cleanser"
                onDelete={() => alert('Delete clicked!')}
                onDragStart={() => alert('Drag started!')}
              />
              <RoutineCard
                company="The Ordinary"
                productName="Niacinamide 10% + Zinc 1%"
                category="Serum"
                onDelete={() => alert('Delete clicked!')}
                onDragStart={() => alert('Drag started!')}
              />
              <RoutineCard
                company="Neutrogena"
                productName="Ultra Sheer Dry-Touch Sunscreen SPF 55"
                category="Sunscreen"
                onDragStart={() => alert('Drag started!')}
              />
            </div>
          </div>

          <div className="card-variants">
            <Typography variant="h3">Routine Card with Different Categories</Typography>
            <div className="routine-card-examples">
              <RoutineCard
                company="La Roche-Posay"
                productName="Double Repair Face Moisturizer"
                category="Moisturizer"
                onDelete={() => alert('Delete clicked!')}
                onDragStart={() => alert('Drag started!')}
              />
              <RoutineCard
                company="Paula's Choice"
                productName="2% BHA Liquid Exfoliant"
                category="Exfoliant"
                onDelete={() => alert('Delete clicked!')}
                onDragStart={() => alert('Drag started!')}
              />
              <RoutineCard
                company="The Inkey List"
                productName="Retinol Serum"
                category="Retinol"
                onDragStart={() => alert('Drag started!')}
              />
            </div>
          </div>

          <div className="mobile-examples">
            <Typography variant="h3">Mobile Routine Cards</Typography>
            <div className="mobile-preview">
              <div className="mobile-frame">
                <div className="mobile-content">
                  <div className="mobile-routine-list">
                    <RoutineCard
                      company="CeraVe"
                      productName="Hydrating Facial Cleanser"
                      category="Cleanser"
                      onDelete={() => alert('Delete clicked!')}
                      onDragStart={() => alert('Drag started!')}
                    />
                    <RoutineCard
                      company="The Ordinary"
                      productName="Niacinamide 10% + Zinc 1%"
                      category="Serum"
                      onDelete={() => alert('Delete clicked!')}
                      onDragStart={() => alert('Drag started!')}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Popover Section */}
      <section className="design-system-section">
        <Typography variant="h2">Popovers</Typography>
        <div className="popover-test-area">
          <div className="popover-examples">
            <Typography variant="h3">Basic Popover</Typography>
            <div className="popover-demo">
              <Button 
                ref={basicTriggerRef}
                variant="primary" 
                onClick={() => setPopoverStates(prev => ({ ...prev, basic: !prev.basic }))}
              >
                Open Popover
              </Button>
              <Popover
                options={popoverOptions}
                isOpen={popoverStates.basic}
                onClose={() => setPopoverStates(prev => ({ ...prev, basic: false }))}
                position="bottom"
                triggerRef={basicTriggerRef}
              />
            </div>
          </div>

          <div className="popover-variants">
            <Typography variant="h3">Position Variants</Typography>
            <div className="popover-row">
              <div className="popover-demo">
                <Button 
                  ref={topTriggerRef}
                  variant="secondary" 
                  onClick={() => setPopoverStates(prev => ({ ...prev, top: !prev.top }))}
                >
                  Top
                </Button>
                <Popover
                  options={popoverOptions}
                  isOpen={popoverStates.top}
                  onClose={() => setPopoverStates(prev => ({ ...prev, top: false }))}
                  position="top"
                  triggerRef={topTriggerRef}
                />
              </div>
              <div className="popover-demo">
                <Button 
                  ref={bottomTriggerRef}
                  variant="secondary" 
                  onClick={() => setPopoverStates(prev => ({ ...prev, bottom: !prev.bottom }))}
                >
                  Bottom
                </Button>
                <Popover
                  options={popoverOptions}
                  isOpen={popoverStates.bottom}
                  onClose={() => setPopoverStates(prev => ({ ...prev, bottom: false }))}
                  position="bottom"
                  triggerRef={bottomTriggerRef}
                />
              </div>
              <div className="popover-demo">
                <Button 
                  ref={leftTriggerRef}
                  variant="secondary" 
                  onClick={() => setPopoverStates(prev => ({ ...prev, left: !prev.left }))}
                >
                  Left
                </Button>
                <Popover
                  options={popoverOptions}
                  isOpen={popoverStates.left}
                  onClose={() => setPopoverStates(prev => ({ ...prev, left: false }))}
                  position="left"
                  triggerRef={leftTriggerRef}
                />
              </div>
              <div className="popover-demo">
                <Button 
                  ref={rightTriggerRef}
                  variant="secondary" 
                  onClick={() => setPopoverStates(prev => ({ ...prev, right: !prev.right }))}
                >
                  Right
                </Button>
                <Popover
                  options={popoverOptions}
                  isOpen={popoverStates.right}
                  onClose={() => setPopoverStates(prev => ({ ...prev, right: false }))}
                  position="right"
                  triggerRef={rightTriggerRef}
                />
              </div>
            </div>
          </div>

          <div className="popover-options">
            <Typography variant="h3">Options with Different Labels</Typography>
            <div className="popover-row">
              <div className="popover-demo">
                <Button 
                  ref={optionsTriggerRef}
                  variant="text" 
                  onClick={() => setPopoverStates(prev => ({ ...prev, options: !prev.options }))}
                >
                  Show Options
                </Button>
                <Popover
                  options={[
                    { id: '1', label: 'Edit Product', onClick: () => alert('Edit clicked!') },
                    { id: '2', label: 'Delete Product', onClick: () => alert('Delete clicked!') },
                    { id: '3', label: 'Duplicate Product', onClick: () => alert('Duplicate clicked!') },
                    { id: '4', label: 'Share Product', onClick: () => alert('Share clicked!') }
                  ]}
                  isOpen={popoverStates.options}
                  onClose={() => setPopoverStates(prev => ({ ...prev, options: false }))}
                  position="bottom"
                  triggerRef={optionsTriggerRef}
                />
              </div>
            </div>
          </div>

          <div className="popover-edge-cases">
            <Typography variant="h3">Edge Cases & Intelligent Positioning</Typography>
            <Typography variant="body-small" style={{ marginBottom: '16px', color: 'var(--color-gray)' }}>
              The popover automatically repositions based on available viewport space. 
              Try resizing the window or scrolling to see it adapt.
            </Typography>
            <div className="popover-row">
              <div className="popover-demo">
                <Button 
                  variant="secondary" 
                  onClick={() => setPopoverStates(prev => ({ ...prev, basic: !prev.basic }))}
                >
                  Test Auto-Reposition
                </Button>
                <Popover
                  options={[
                    { id: '1', label: 'This popover will reposition', onClick: () => alert('Clicked!') },
                    { id: '2', label: 'when there is not enough space', onClick: () => alert('Clicked!') },
                    { id: '3', label: 'in the viewport', onClick: () => alert('Clicked!') },
                    { id: '4', label: 'Try resizing the window!', onClick: () => alert('Clicked!') }
                  ]}
                  isOpen={popoverStates.basic}
                  onClose={() => setPopoverStates(prev => ({ ...prev, basic: false }))}
                  position="bottom"
                  triggerRef={basicTriggerRef}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dialog Section */}
      <section className="design-system-section">
        <Typography variant="h2">Dialog</Typography>
        <div className="dialog-test-area">
          <div className="dialog-variants">
            <Typography variant="h3">Dialog Variants</Typography>
            <div className="dialog-row">
              <Button 
                variant="secondary" 
                onClick={() => setDialogStates(prev => ({ ...prev, basic: true }))}
              >
                Basic Dialog
              </Button>
              <Button 
                variant="secondary" 
                onClick={() => setDialogStates(prev => ({ ...prev, withContent: true }))}
              >
                Dialog with Content
              </Button>
              <Button 
                variant="secondary" 
                onClick={() => setDialogStates(prev => ({ ...prev, withActions: true }))}
              >
                Dialog with Actions
              </Button>
              <Button 
                variant="secondary" 
                onClick={() => setDialogStates(prev => ({ ...prev, activeIngredients: true }))}
              >
                Active Ingredients Dialog
              </Button>
            </div>
          </div>
        </div>

        {/* Basic Dialog */}
        <ModalDialog 
          isOpen={dialogStates.basic} 
          onClose={() => setDialogStates(prev => ({ ...prev, basic: false }))}
          title="Basic Dialog"
          primaryButton={{
            label: "Confirm",
            onClick: () => setDialogStates(prev => ({ ...prev, basic: false }))
          }}
          secondaryButton={{
            label: "Cancel",
            onClick: () => setDialogStates(prev => ({ ...prev, basic: false }))
          }}
        >
          <Typography variant="body">
            This is a basic dialog with minimal content.
          </Typography>
        </ModalDialog>

        {/* Dialog with Content */}
        <ModalDialog 
          isOpen={dialogStates.withContent} 
          onClose={() => setDialogStates(prev => ({ ...prev, withContent: false }))}
          title="Dialog with Content"
          primaryButton={{
            label: "Send",
            onClick: () => setDialogStates(prev => ({ ...prev, withContent: false }))
          }}
          secondaryButton={{
            label: "Cancel",
            onClick: () => setDialogStates(prev => ({ ...prev, withContent: false }))
          }}
          maxWidth="500px"
        >
          <Typography variant="body" style={{ marginBottom: '16px' }}>
            This dialog contains more detailed content including form elements and longer text.
          </Typography>
          <div style={{ marginBottom: '16px' }}>
            <TextInput
              label="Email Address"
              value={textInputStates.email}
              onChange={(e) => setTextInputStates(prev => ({ ...prev, email: e.target.value }))}
              placeholder="Enter your email"
            />
          </div>
          <div>
            <TextInput
              label="Message"
              value={textInputStates.withLabel}
              onChange={(e) => setTextInputStates(prev => ({ ...prev, withLabel: e.target.value }))}
              placeholder="Enter your message"
            />
          </div>
        </ModalDialog>

        {/* Dialog with Actions */}
        <ModalDialog 
          isOpen={dialogStates.withActions} 
          onClose={() => setDialogStates(prev => ({ ...prev, withActions: false }))}
          title="Confirm Action"
          primaryButton={{
            label: "Delete",
            onClick: () => setDialogStates(prev => ({ ...prev, withActions: false }))
          }}
          secondaryButton={{
            label: "Cancel",
            onClick: () => setDialogStates(prev => ({ ...prev, withActions: false }))
          }}
          maxWidth="450px"
        >
          <Typography variant="body">
            Are you sure you want to delete this item? This action cannot be undone.
          </Typography>
        </ModalDialog>

        {/* Active Ingredients Dialog */}
        <ResponsiveDialog
          type="activeIngredients"
          isOpen={dialogStates.activeIngredients}
          onClose={() => setDialogStates(prev => ({ ...prev, activeIngredients: false }))}
          onActiveClick={(active) => {
            console.log('Active clicked:', active);
            // You can add more functionality here
          }}
        />
      </section>

      {/* Interactive Test Section */}
      <section className="design-system-section">
        <Typography variant="h2">Interactive Tests</Typography>
        <div className="interactive-tests">
          <div className="test-card">
            <Typography variant="h3">Clickable Icons</Typography>
            <div className="interactive-icons">
              <Icon 
                name="idea" 
                size="large" 
                onClick={() => alert('Idea clicked!')}
                style={{ cursor: 'pointer', margin: '0 8px' }}
              />
              <Icon 
                name="adjustments" 
                size="large" 
                onClick={() => alert('Adjustments clicked!')}
                style={{ cursor: 'pointer', margin: '0 8px' }}
              />
              <Icon 
                name="close" 
                size="large" 
                onClick={() => alert('Close clicked!')}
                style={{ cursor: 'pointer', margin: '0 8px' }}
              />
            </div>
          </div>

          <div className="test-card">
            <Typography variant="h3">Button Interactions</Typography>
            <div className="interactive-buttons">
              <Button 
                variant="primary" 
                onClick={() => alert('Primary button clicked!')}
              >
                Click Me
              </Button>
              <Button 
                variant="secondary" 
                onClick={() => alert('Secondary button clicked!')}
              >
                Click Me Too
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Loading Spinner Section */}
      <section className="design-system-section">
        <Typography variant="h2">Loading Spinner</Typography>
        <div className="loading-spinner-test-area">
          <div className="loading-spinner-sizes">
            <Typography variant="h3">Sizes</Typography>
            <div className="loading-spinner-row">
              <div className="loading-spinner-demo">
                <Typography variant="body-small-bold">Small</Typography>
                <LoadingSpinner size="small" />
              </div>
              <div className="loading-spinner-demo">
                <Typography variant="body-small-bold">Medium (Default)</Typography>
                <LoadingSpinner size="medium" />
              </div>
              <div className="loading-spinner-demo">
                <Typography variant="body-small-bold">Large</Typography>
                <LoadingSpinner size="large" />
              </div>
            </div>
          </div>

          <div className="loading-spinner-with-text">
            <Typography variant="h3">With Text</Typography>
            <div className="loading-spinner-row">
              <div className="loading-spinner-demo">
                <Typography variant="body-small-bold">Small with Text</Typography>
                <LoadingSpinner size="small" text="Loading..." />
              </div>
              <div className="loading-spinner-demo">
                <Typography variant="body-small-bold">Medium with Text</Typography>
                <LoadingSpinner size="medium" text="Checking authentication..." />
              </div>
              <div className="loading-spinner-demo">
                <Typography variant="body-small-bold">Large with Text</Typography>
                <LoadingSpinner size="large" text="Processing your request..." />
              </div>
            </div>
          </div>

          <div className="loading-spinner-use-cases">
            <Typography variant="h3">Common Use Cases</Typography>
            <div className="loading-spinner-examples">
              <div className="loading-spinner-example">
                <Typography variant="body-small-bold">Page Loading</Typography>
                <LoadingSpinner size="medium" text="Loading page..." />
              </div>
              <div className="loading-spinner-example">
                <Typography variant="body-small-bold">Data Fetching</Typography>
                <LoadingSpinner size="medium" text="Fetching data..." />
              </div>
              <div className="loading-spinner-example">
                <Typography variant="body-small-bold">Authentication</Typography>
                <LoadingSpinner size="medium" text="Checking authentication..." />
              </div>
              <div className="loading-spinner-example">
                <Typography variant="body-small-bold">Saving</Typography>
                <LoadingSpinner size="small" text="Saving..." />
              </div>
            </div>
          </div>

          <div className="loading-spinner-variants">
            <Typography variant="h3">Different Text Examples</Typography>
            <div className="loading-spinner-text-examples">
              <LoadingSpinner text="Loading your cabinet..." />
              <LoadingSpinner text="Redirecting to login..." />
              <LoadingSpinner text="Loading product..." />
              <LoadingSpinner text="Updating routine..." />
            </div>
          </div>
        </div>
      </section>

      {/* Responsive Test Section */}
      <section className="design-system-section">
        <Typography variant="h2">Responsive Design Test</Typography>
        <div className="responsive-test">
          <Typography variant="body">
            Resize your browser window to see how components adapt to different screen sizes.
            The typography, buttons, and spacing should automatically adjust for mobile devices.
          </Typography>
          
          <div className="responsive-grid">
            <div className="responsive-item">
              <Typography variant="h3">Typography</Typography>
              <Typography variant="h1">H1 Heading</Typography>
              <Typography variant="h2">H2 Heading</Typography>
              <Typography variant="body">Body text that should be responsive</Typography>
            </div>
            
            <div className="responsive-item">
              <Typography variant="h3">Buttons</Typography>
              <Button variant="primary">Primary</Button>
              <Button variant="secondary">Secondary</Button>
            </div>
            
            <div className="responsive-item">
              <Typography variant="h3">Icons</Typography>
              <Icon name="search" size="large" />
              <Icon name="user" size="large" />
              <Icon name="settings" size="large" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DesignSystem; 